import { action, computed, makeObservable, observable } from "mobx";
import { ClassifierImplementationType } from "models/ClassifierImplementationModel";
import type { LLMModel } from "models/LLMs";
import { LLMClassifierImplementationConfigurationType } from "models/LLMs/LLMClassifierImplementation";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import { LLMClassifierImplementation as LLMClassifierImplementationModel } from "models/LLMs/LLMClassifierImplementation";
import type { LLMClassifierRubric } from "models/LLMs/LLMClassifierImplementation";
import type { ILLMClassifierConfigurationBuilder } from "./LLMClassifierConfigurationBuilder";
import { LLMClassifierStandardBuilder } from "./LLMClassifierStandardBuilder";
import { LLMClassifierLogicChainBuilder } from "./LLMClassifierLogicChainBuilder";
import { v4 as uuid } from "uuid";
import { ApprovalStatus } from "models/LLMs/LLMClassifierApproval";

export class LLMClassifierImplementation {
    public readonly id: string;
    public readonly implementationType: ClassifierImplementationType.LLM;
    public readonly classifierId: string;

    @observable public isActive?: boolean;
    @observable public createdBy?: string;
    @observable public createdOn?: string;
    @observable public modifiedBy?: string;
    @observable public modifiedOn?: string;
    @observable public deletedOn?: string;

    @observable public versionNumber?: number;
    @observable public approvalStatus?: ApprovalStatus;
    @observable public llmModelId?: string;
    @observable public llmModel?: LLMModel;
    @observable public llmTargetSpeaker?: string;

    @observable public llmPurpose?: LLMClassifierPurpose;

    @observable public type: LLMClassifierImplementationConfigurationType;

    @observable public rubric?: LLMClassifierRubric;
    /** The rubric this version was saved with. Unchanged by edits until the next save. */
    public readonly savedRubricId?: string;

    public readonly standardBuilder: LLMClassifierStandardBuilder;
    public readonly logicChainBuilder: LLMClassifierLogicChainBuilder;

    /**
     * Creates an instance from an existing implementation
     * @param implementation
     */
    constructor(implementation: LLMClassifierImplementationModel);
    /**
     * Creates a new implementation instance for a given classifier
     * @param classifierId
     */
    constructor(classifierId: string);
    constructor(
        implementationOrClassifierId: LLMClassifierImplementationModel | string,
    ) {
        makeObservable(this);

        this.type = LLMClassifierImplementationConfigurationType.LogicChaining;
        this.standardBuilder = new LLMClassifierStandardBuilder();
        this.logicChainBuilder = new LLMClassifierLogicChainBuilder();
        this.implementationType = ClassifierImplementationType.LLM;

        if (typeof implementationOrClassifierId === "string") {
            const classifierId = implementationOrClassifierId;
            this.id = uuid();
            this.classifierId = classifierId;
        } else {
            const implementation = implementationOrClassifierId;
            if (!implementation.classifierId) {
                throw new Error("Implementation must have a classifier id");
            }

            this.id = implementation.id;
            this.isActive = implementation.isActive;
            this.createdBy = implementation.createdBy;
            this.createdOn = implementation.createdOn;
            this.modifiedBy = implementation.modifiedBy;
            this.modifiedOn = implementation.modifiedOn;
            this.deletedOn = implementation.deletedOn;
            this.versionNumber = implementation.versionNumber;
            this.approvalStatus = implementation.approvalStatus;
            this.classifierId = implementation.classifierId;
            this.llmModelId = implementation.llmModelId;
            this.llmModel = implementation.llmModel;
            this.llmTargetSpeaker = implementation.llmTargetSpeaker;
            this.llmPurpose =
                implementation.llmPurpose ?? LLMClassifierPurpose.Quality;
            this.type = implementation.type;
            this.savedRubricId = implementation.rubricId;
            // Only the fields a save sends, so the unsaved-changes check compares like with like.
            if (implementation.rubric) {
                this.rubric = {
                    questionId: implementation.rubric.questionId,
                    taxonomy: { ...implementation.rubric.taxonomy },
                };
            }

            switch (implementation.type) {
                case LLMClassifierImplementationConfigurationType.PromptOnly:
                    this.standardBuilder = new LLMClassifierStandardBuilder(
                        implementation.configuration,
                        implementation.llmResultDefinitions,
                    );
                    break;
                case LLMClassifierImplementationConfigurationType.LogicChaining:
                    this.logicChainBuilder = new LLMClassifierLogicChainBuilder(
                        implementation.configuration,
                    );
                    break;
                default:
                    throw new Error(
                        "Unsupported classifier implementation configuration type",
                    );
            }
        }
    }

    @computed get configurationBuilder(): ILLMClassifierConfigurationBuilder {
        switch (this.type) {
            case LLMClassifierImplementationConfigurationType.PromptOnly:
                return this.standardBuilder;
            case LLMClassifierImplementationConfigurationType.LogicChaining:
                return this.logicChainBuilder;
            default:
                throw new Error(
                    "Unsupported classifier implementation configuration type",
                );
        }
    }

    @action public setVersionNumber(versionNumber: number) {
        this.versionNumber = versionNumber;
    }

    /** Whether this version is the deployed one. Publishing and unpublishing move it. */
    @action public setIsActive(isActive: boolean) {
        this.isActive = isActive;
    }

    @action public setCreatedOn(createdOn?: string) {
        this.createdOn = createdOn;
    }

    @action public setLLMModel(llmModel: LLMModel) {
        this.llmModelId = llmModel.id;
        this.llmModel = llmModel;
    }

    @action public setLLMTargetSpeaker(llmTargetSpeaker: string) {
        this.llmTargetSpeaker = llmTargetSpeaker;
    }

    @action public setRubric(rubric?: LLMClassifierRubric) {
        this.rubric = rubric;
    }

    @action public setLLMPurpose(llmPurpose: LLMClassifierPurpose) {
        this.llmPurpose = llmPurpose;

        // Every purpose builds with the logic chain; the standard builder is only kept to view and edit older versions.
        this.setType(LLMClassifierImplementationConfigurationType.LogicChaining);
    }

    @action public setType<
        T extends LLMClassifierImplementationConfigurationType,
    >(type: T): ConfigurationTypeToBuilder[T] {
        this.type = type;
        return this.configurationBuilder as ConfigurationTypeToBuilder[T];
    }

    /** Whether enough is filled in to serialize via `toObject`. Separate from saving: an
     *  incomplete implementation must still be diffable for unsaved-changes tracking. */
    public isComplete(): boolean {
        return (
            !!this.llmModelId &&
            !!this.llmModel &&
            this.llmPurpose !== undefined
        );
    }

    public toObject(): LLMClassifierImplementationModel {
        if (!this.isComplete()) {
            throw new Error("LLM model and purpose are required");
        }

        // @ts-expect-error It's angry about the `type` and `configuration` properties because of discriminated union
        return {
            id: this.id,
            implementationType: this.implementationType,
            isActive: this.isActive,
            createdBy: this.createdBy,
            createdOn: this.createdOn,
            modifiedBy: this.modifiedBy,
            modifiedOn: this.modifiedOn,
            deletedOn: this.deletedOn,
            versionNumber: this.versionNumber,
            approvalStatus: this.approvalStatus,
            classifierId: this.classifierId,
            llmModelId: this.llmModelId!,
            llmModel: this.llmModel!,
            llmTargetSpeaker: this.llmTargetSpeaker,
            llmPurpose: this.llmPurpose!,
            llmResultDefinitions:
                this.type ===
                LLMClassifierImplementationConfigurationType.PromptOnly
                    ? this.standardBuilder.llmResultDefinitions
                    : undefined,
            type: this.type,
            configuration: this.configurationBuilder.getConfiguration(),
            rubric: this.rubric,
        };
    }

    public isStandard(): boolean {
        return (
            this.type ===
            LLMClassifierImplementationConfigurationType.PromptOnly
        );
    }

    public isLogicChain(): boolean {
        return (
            this.type ===
            LLMClassifierImplementationConfigurationType.LogicChaining
        );
    }
}

type ConfigurationTypeToBuilder = {
    [LLMClassifierImplementationConfigurationType.PromptOnly]: LLMClassifierStandardBuilder;
    [LLMClassifierImplementationConfigurationType.LogicChaining]: LLMClassifierLogicChainBuilder;
};
